import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import * as XLSX from 'xlsx';
import CrapExcel from '../../api/crapExcel';
import { useDispatch } from 'react-redux';
import { show } from '../../reducers/notificationSlice';

function formatBytes(n) {
    if (n == null || n === '') return '—';
    const num = Number(n);
    if (Number.isNaN(num)) return '—';
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / (1024 * 1024)).toFixed(2)} MB`;
}

function displayNameFromKey(key) {
    if (!key) return '';
    const segment = key.split('/').pop() || key;
    const idx = segment.indexOf('_');
    if (idx === -1) return segment;
    return segment.slice(idx + 1) || segment;
}

/** Safety cap so very large sheets stay responsive in the browser */
const MAX_PREVIEW_ROWS_PER_SHEET = 10000;
const REPORTS_PAGE_SIZE = 10;
const MAX_GENERATED_REPORT_PAGE_DRILLDOWN = 6;

function shouldHaveAssociatedReport(formRow) {
    return Boolean(String(formRow?.reportID ?? '').trim());
}

function getPaymentDetails(row) {
    if (row?.paymentDetails && typeof row.paymentDetails === 'object') {
        return row.paymentDetails;
    }
    if (row?.orderInfo && typeof row.orderInfo === 'object') {
        return row.orderInfo;
    }
    if (typeof row?.orderInfo === 'string') {
        const parts = row.orderInfo
            .split(/<>+/)
            .map((part) => part.trim())
            .filter(Boolean);
        for (let index = parts.length - 1; index >= 0; index -= 1) {
            try {
                return JSON.parse(parts[index]);
            } catch (e) {
                // Keep walking backwards until a valid payment event is found.
            }
        }
    }
    return null;
}

function getPaymentSummary(row) {
    const paymentDetails = getPaymentDetails(row) || {};
    return {
        amount: paymentDetails.amount || '—',
        mode: paymentDetails.payment_mode || paymentDetails.card_name || row?.paymentKind || '—',
        orderStatus: paymentDetails.order_status || '—',
        transactionDate: paymentDetails.trans_date || '—',
        trackingID: paymentDetails.tracking_id || '—',
        orderID: paymentDetails.order_id || row?.orderID || '—',
        bankRef: paymentDetails.bank_ref_no || '—',
    };
}

function getReportVisibilityMeta(row) {
    const paymentSummary = getPaymentSummary(row);
    const hasAssociatedReport = Boolean(row?.associatedReport);
    const hasExpectedReport = shouldHaveAssociatedReport(row);
    const paymentSucceeded =
        String(row?.paymentStatus ?? '').trim().toUpperCase() === 'SUCCESS' ||
        String(paymentSummary.orderStatus ?? '').trim().toUpperCase() === 'SUCCESS';

    if (hasAssociatedReport) {
        return {
            label: 'Success',
            color: 'success',
            helperText: 'Report generated and visible',
        };
    }
    if (hasExpectedReport && paymentSucceeded) {
        return {
            label: 'Failure',
            color: 'error',
            helperText: 'Payment succeeded but report is missing',
        };
    }
    if (hasExpectedReport) {
        return {
            label: 'Pending',
            color: 'warning',
            helperText: 'Waiting for successful payment',
        };
    }
    return {
        label: 'Not started',
        color: 'default',
        helperText: 'No report requested yet',
    };
}

function mergeUniqueGeneratedReports(existingItems, incomingItems) {
    const existing = Array.isArray(existingItems) ? existingItems : [];
    const incoming = Array.isArray(incomingItems) ? incomingItems : [];
    if (incoming.length === 0) return existing;

    const byID = new Set(existing.map((item) => String(item?.id ?? '')));
    const merged = [...existing];
    for (const item of incoming) {
        const id = String(item?.id ?? '');
        if (!id || byID.has(id)) continue;
        byID.add(id);
        merged.push(item);
    }
    return merged;
}

function buildGeneratedReportByFormID(items) {
    const map = {};
    for (const item of items || []) {
        const formSubmissionID = String(item?.formSubmissionID ?? '').trim();
        if (!formSubmissionID) continue;
        map[formSubmissionID] = item;
    }
    return map;
}

function getMissingAssociatedReportFormIDs(formRows, generatedReportByFormID) {
    const missing = [];
    for (const row of formRows || []) {
        const formID = String(row?.id ?? '').trim();
        if (!formID || !shouldHaveAssociatedReport(row)) continue;
        if (!generatedReportByFormID?.[formID]) {
            missing.push(formID);
        }
    }
    return missing;
}

function attachAssociatedReportsToForms(formRows, generatedReportByFormID) {
    return (formRows || []).map((row) => ({
        ...row,
        associatedReport: generatedReportByFormID?.[String(row?.id ?? '').trim()] || null,
    }));
}

function prettyJson(value) {
    if (value == null || value === '') return '—';
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return '—';
        try {
            return JSON.stringify(JSON.parse(trimmed), null, 2);
        } catch (e) {
            return trimmed;
        }
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch (e) {
        return String(value);
    }
}

function buildFullSheetPreview(workbook) {
    return workbook.SheetNames.map((name) => {
        const ws = workbook.Sheets[name];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        const totalRows = raw.length;
        const truncated = totalRows > MAX_PREVIEW_ROWS_PER_SHEET;
        const slice = truncated ? raw.slice(0, MAX_PREVIEW_ROWS_PER_SHEET) : raw;
        if (slice.length === 0) {
            return { name, rows: [], colCount: 0, totalRows: 0, truncated: false };
        }
        const maxCols = Math.max(1, ...slice.map((r) => (Array.isArray(r) ? r.length : 0)));
        const rows = slice.map((r) => {
            const arr = Array.isArray(r) ? [...r] : [];
            while (arr.length < maxCols) arr.push('');
            return arr.map((c) => (c === '' || c == null ? '' : String(c)));
        });
        return { name, rows, colCount: maxCols, totalRows, truncated };
    });
}

function parseWorkbookFromArrayBuffer(ab) {
    return XLSX.read(ab, { type: 'array' });
}

/** Full grid per sheet (all rows up to cap); first row shown as sticky header */
function ExcelPreviewSheetTabs({ sheets }) {
    const [tab, setTab] = useState(0);

    useEffect(() => {
        setTab(0);
    }, [sheets]);

    if (!sheets?.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                No sheets found.
            </Typography>
        );
    }

    const safeTab = Math.min(Math.max(0, tab), sheets.length - 1);
    const sheet = sheets[safeTab];
    const rows = sheet.rows || [];

    return (
        <Box>
            <Tabs
                value={safeTab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 1.5,
                    minHeight: 40,
                    '& .MuiTab-root': { textTransform: 'none', minHeight: 40, py: 1 },
                }}
            >
                {sheets.map((s) => (
                    <Tab key={s.name} label={s.name} />
                ))}
            </Tabs>
            {sheet.truncated ? (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    This sheet has {sheet.totalRows.toLocaleString()} rows. Showing the first {rows.length.toLocaleString()}{' '}
                    rows in the preview.
                </Alert>
            ) : null}
            <TableContainer
                sx={{
                    maxHeight: { xs: 360, sm: 520 },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'auto',
                }}
            >
                <Table size="small" stickyHeader sx={{ minWidth: 400 }}>
                    {rows.length > 0 ? (
                        <TableHead>
                            <TableRow>
                                {rows[0].map((cell, ci) => (
                                    <TableCell
                                        key={`h-${ci}`}
                                        sx={{
                                            fontWeight: 700,
                                            whiteSpace: 'nowrap',
                                            minWidth: 72,
                                            maxWidth: 220,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {cell || `Col ${ci + 1}`}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                    ) : null}
                    <TableBody>
                        {rows.length <= 1 ? (
                            <TableRow>
                                <TableCell colSpan={Math.max(1, sheet.colCount || 1)}>
                                    {rows.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Empty sheet
                                        </Typography>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Header row only (no data rows)
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.slice(1).map((row, ri) => (
                                <TableRow key={ri} hover>
                                    {row.map((cell, ci) => (
                                        <TableCell
                                            key={ci}
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                maxWidth: 220,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {cell}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {rows.length > 1 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {rows.length - 1} data row{rows.length - 1 === 1 ? '' : 's'} (plus header)
                    {sheet.totalRows != null ? ` · ${sheet.totalRows.toLocaleString()} total row(s) in file` : ''}
                </Typography>
            ) : null}
        </Box>
    );
}

export default function ReportData() {
    const dispatch = useDispatch();
    const inputRef = useRef(null);

    const [, setWorkbook] = useState(null);
    const [sheetPreview, setSheetPreview] = useState([]);
    const [stagedFile, setStagedFile] = useState(null);
    const [parseLoading, setParseLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadReviewOpen, setUploadReviewOpen] = useState(false);

    const [storeItems, setStoreItems] = useState([]);
    const [storeNextToken, setStoreNextToken] = useState(null);
    const [storeLoading, setStoreLoading] = useState(false);
    const [storeLoadingMore, setStoreLoadingMore] = useState(false);

    const [storePreviewOpen, setStorePreviewOpen] = useState(false);
    const [storePreviewTitle, setStorePreviewTitle] = useState('');
    const [storePreviewKey, setStorePreviewKey] = useState(null);
    const [storePreviewSheets, setStorePreviewSheets] = useState([]);
    const [storePreviewLoading, setStorePreviewLoading] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTargetKey, setDeleteTargetKey] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [mainTab, setMainTab] = useState(0);
    const [reportsRows, setReportsRows] = useState([]);
    const [reportsLastKey, setReportsLastKey] = useState(null);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [reportsQueryDone, setReportsQueryDone] = useState(false);
    const [reportsPage, setReportsPage] = useState(0);
    const [reportsPageCursors, setReportsPageCursors] = useState([null]);
    const [generatedReportsRows, setGeneratedReportsRows] = useState([]);
    const [generatedReportsLastKey, setGeneratedReportsLastKey] = useState(null);
    const [regeneratingReportFormID, setRegeneratingReportFormID] = useState('');

    const [reportDetailsOpen, setReportDetailsOpen] = useState(false);
    const [reportDetailsLoading, setReportDetailsLoading] = useState(false);
    const [reportDetailsError, setReportDetailsError] = useState('');
    const [reportDetailsRow, setReportDetailsRow] = useState(null);
    const [reportDetailsPayload, setReportDetailsPayload] = useState(null);

    const refreshStoreList = useCallback(async () => {
        setStoreLoading(true);
        try {
            const { data } = await CrapExcel.listObjects({ maxKeys: 25 });
            setStoreItems(data?.items || []);
            setStoreNextToken(data?.nextContinuationToken || null);
        } catch (err) {
            dispatch(
                show({
                    message: err?.response?.data?.message || err?.message || 'Failed to list files',
                    open: true,
                    severity: 'error',
                })
            );
        } finally {
            setStoreLoading(false);
        }
    }, [dispatch]);

    const loadMoreStore = useCallback(async () => {
        if (!storeNextToken) return;
        setStoreLoadingMore(true);
        try {
            const { data } = await CrapExcel.listObjects({
                maxKeys: 25,
                continuationToken: storeNextToken,
            });
            const items = data?.items || [];
            setStoreItems((prev) => [...prev, ...items]);
            setStoreNextToken(data?.nextContinuationToken || null);
        } catch (err) {
            dispatch(
                show({
                    message: err?.response?.data?.message || err?.message || 'Failed to list files',
                    open: true,
                    severity: 'error',
                })
            );
        } finally {
            setStoreLoadingMore(false);
        }
    }, [dispatch, storeNextToken]);

    useEffect(() => {
        refreshStoreList();
    }, [refreshStoreList]);

    const readExcelFile = useCallback((file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const arrayBuffer = event.target.result;
                    const wb = parseWorkbookFromArrayBuffer(arrayBuffer);
                    resolve(wb);
                } catch (e) {
                    reject(e);
                }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
        });
    }, []);

    const resetStagedUpload = () => {
        setStagedFile(null);
        setSheetPreview([]);
        setWorkbook(null);
        setUploadReviewOpen(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleFiles = async (fileList) => {
        const file = fileList?.[0];
        if (!file) return;
        if (!file.size || file.size <= 0) {
            dispatch(
                show({
                    message: 'Empty files (0 B) cannot be uploaded.',
                    open: true,
                    severity: 'warning',
                })
            );
            if (inputRef.current) inputRef.current.value = '';
            return;
        }
        setParseLoading(true);
        setStagedFile(null);
        setSheetPreview([]);
        setWorkbook(null);
        try {
            const wb = await readExcelFile(file);
            setWorkbook(wb);
            setSheetPreview(buildFullSheetPreview(wb));
            setStagedFile(file);
            setUploadReviewOpen(true);
        } catch (e) {
            dispatch(
                show({
                    message: 'Could not read Excel file',
                    open: true,
                    severity: 'error',
                })
            );
            resetStagedUpload();
        } finally {
            setParseLoading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const uploadToStore = async () => {
        if (!stagedFile) {
            dispatch(show({ message: 'No file to upload', open: true, severity: 'warning' }));
            return;
        }
        if (!stagedFile.size || stagedFile.size <= 0) {
            dispatch(
                show({
                    message: 'Empty files (0 B) cannot be uploaded.',
                    open: true,
                    severity: 'warning',
                })
            );
            return;
        }
        setUploading(true);
        try {
            const { data } = await CrapExcel.presignUpload(stagedFile.name, stagedFile.size);
            const { uploadUrl, contentType } = data;
            const res = await fetch(uploadUrl, {
                method: 'PUT',
                body: stagedFile,
                headers: {
                    'Content-Type': contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
            });
            if (!res.ok) {
                throw new Error(`Upload failed (${res.status})`);
            }
            dispatch(
                show({ message: 'File uploaded to excel store', open: true, severity: 'success' })
            );
            setUploadReviewOpen(false);
            setStagedFile(null);
            setSheetPreview([]);
            await refreshStoreList();
        } catch (err) {
            dispatch(
                show({
                    message: err?.message || 'Upload failed',
                    open: true,
                    severity: 'error',
                })
            );
        } finally {
            setUploading(false);
        }
    };

    const downloadObject = async (key) => {
        try {
            const { data } = await CrapExcel.presignDownload(key);
            const url = data?.downloadUrl;
            if (!url) throw new Error('No download URL');
            const a = document.createElement('a');
            a.href = url;
            a.download = displayNameFromKey(key);
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            dispatch(
                show({
                    message: err?.response?.data?.message || err?.message || 'Download failed',
                    open: true,
                    severity: 'error',
                })
            );
        }
    };

    const closeStorePreview = () => {
        setStorePreviewOpen(false);
        setStorePreviewKey(null);
    };

    const openStorePreview = async (key) => {
        const title = displayNameFromKey(key);
        setStorePreviewTitle(title);
        setStorePreviewKey(key);
        setStorePreviewOpen(true);
        setStorePreviewSheets([]);
        setStorePreviewLoading(true);
        try {
            const { data } = await CrapExcel.presignDownload(key);
            const url = data?.downloadUrl;
            if (!url) throw new Error('No download URL');
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
            const ab = await res.arrayBuffer();
            const wb = parseWorkbookFromArrayBuffer(ab);
            setStorePreviewSheets(buildFullSheetPreview(wb));
        } catch (err) {
            dispatch(
                show({
                    message: err?.message || 'Could not load preview',
                    open: true,
                    severity: 'error',
                })
            );
            closeStorePreview();
        } finally {
            setStorePreviewLoading(false);
        }
    };

    const openDeleteDialog = (key) => {
        setDeleteTargetKey(key);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteFromStore = async () => {
        if (!deleteTargetKey) return;
        setDeleteLoading(true);
        try {
            await CrapExcel.deleteObject(deleteTargetKey);
            dispatch(show({ message: 'File removed from store', open: true, severity: 'success' }));
            setDeleteDialogOpen(false);
            if (deleteTargetKey === storePreviewKey) {
                closeStorePreview();
            }
            setDeleteTargetKey(null);
            await refreshStoreList();
        } catch (err) {
            dispatch(
                show({
                    message: err?.response?.data?.message || err?.message || 'Delete failed',
                    open: true,
                    severity: 'error',
                })
            );
        } finally {
            setDeleteLoading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleFiles(e.dataTransfer.files);
    };

    const hydrateGeneratedReportsForForms = useCallback(async (formRows, initialRows, initialLastKey) => {
        let mergedRows = Array.isArray(initialRows) ? initialRows : [];
        let cursor = initialLastKey || null;
        let generatedByFormID = buildGeneratedReportByFormID(mergedRows);
        let drillDownCount = 0;

        while (
            cursor &&
            drillDownCount < MAX_GENERATED_REPORT_PAGE_DRILLDOWN &&
            getMissingAssociatedReportFormIDs(formRows, generatedByFormID).length > 0
        ) {
            const { data } = await CrapExcel.listAllGeneratedReports({
                limit: REPORTS_PAGE_SIZE,
                lastKey: cursor,
            });
            const payload = data?.data ?? data;
            mergedRows = mergeUniqueGeneratedReports(mergedRows, payload?.items || []);
            generatedByFormID = buildGeneratedReportByFormID(mergedRows);
            cursor = payload?.lastEvaluatedKey || null;
            drillDownCount += 1;
        }

        return {
            rows: mergedRows,
            byFormID: generatedByFormID,
            lastKey: cursor,
        };
    }, []);

    const loadReportsPage = useCallback(async ({ pageIndex, cursor, resetGenerated = false }) => {
        setReportsLoading(true);
        try {
            let generatedRowsSeed = resetGenerated ? [] : generatedReportsRows;
            let generatedLastKeySeed = resetGenerated ? null : generatedReportsLastKey;

            if (resetGenerated) {
                const generatedRes = await CrapExcel.listAllGeneratedReports({
                    limit: REPORTS_PAGE_SIZE,
                    lastKey: null,
                });
                const generatedPayload = generatedRes?.data?.data ?? generatedRes?.data;
                generatedRowsSeed = generatedPayload?.items || [];
                generatedLastKeySeed = generatedPayload?.lastEvaluatedKey || null;
            }

            const { data } = await CrapExcel.listAllFormSubmissions({
                limit: REPORTS_PAGE_SIZE,
                lastKey: cursor,
            });
            const formPayload = data?.data ?? data;
            const formItems = formPayload?.items || [];
            const nextCursor = formPayload?.lastEvaluatedKey || null;

            const hydrated = await hydrateGeneratedReportsForForms(
                formItems,
                generatedRowsSeed,
                generatedLastKeySeed
            );

            setGeneratedReportsRows(hydrated.rows);
            setGeneratedReportsLastKey(hydrated.lastKey);
            setReportsRows(attachAssociatedReportsToForms(formItems, hydrated.byFormID));
            setReportsLastKey(nextCursor);
            setReportsPage(pageIndex);
            setReportsPageCursors((prev) => {
                const next = prev.slice(0, Math.max(prev.length, pageIndex + 1));
                next[pageIndex] = cursor || null;
                if (nextCursor) {
                    next[pageIndex + 1] = nextCursor;
                } else {
                    next.length = pageIndex + 1;
                }
                return next;
            });
            setReportsQueryDone(true);
        } catch (err) {
            dispatch(
                show({
                    message: err?.response?.data?.message || err?.message || 'Failed to load reports',
                    open: true,
                    severity: 'error',
                })
            );
            setReportsRows([]);
            setReportsLastKey(null);
            setReportsQueryDone(false);
            setGeneratedReportsRows([]);
            setGeneratedReportsLastKey(null);
            setReportsPage(0);
            setReportsPageCursors([null]);
        } finally {
            setReportsLoading(false);
        }
    }, [dispatch, generatedReportsLastKey, generatedReportsRows, hydrateGeneratedReportsForForms]);

    const loadReportsFirstPage = useCallback(async () => {
        setReportsRows([]);
        setReportsLastKey(null);
        setReportsQueryDone(false);
        setGeneratedReportsRows([]);
        setGeneratedReportsLastKey(null);
        setReportsPage(0);
        setReportsPageCursors([null]);
        await loadReportsPage({ pageIndex: 0, cursor: null, resetGenerated: true });
    }, [loadReportsPage]);

    const goToReportsPage = useCallback(async (targetPage) => {
        if (targetPage < 0 || reportsLoading) return;
        const cursor = reportsPageCursors[targetPage];
        if (targetPage > reportsPage && !reportsLastKey) return;
        if (targetPage !== 0 && cursor == null) return;
        await loadReportsPage({ pageIndex: targetPage, cursor: cursor || null, resetGenerated: false });
    }, [loadReportsPage, reportsLastKey, reportsLoading, reportsPage, reportsPageCursors]);

    const refreshCurrentReportsPage = useCallback(async () => {
        const cursor = reportsPageCursors[reportsPage] || null;
        await loadReportsPage({ pageIndex: reportsPage, cursor, resetGenerated: true });
    }, [loadReportsPage, reportsPage, reportsPageCursors]);

    const regenerateReport = useCallback(async (row) => {
        const formID = String(row?.id ?? '').trim();
        if (!formID) return;

        setRegeneratingReportFormID(formID);
        try {
            await CrapExcel.regenerateReport(formID);
            dispatch(
                show({
                    message: 'Report generated successfully',
                    open: true,
                    severity: 'success',
                })
            );
            await refreshCurrentReportsPage();
        } catch (err) {
            dispatch(
                show({
                    message: err?.response?.data?.message || err?.message || 'Failed to generate report',
                    open: true,
                    severity: 'error',
                })
            );
        } finally {
            setRegeneratingReportFormID('');
        }
    }, [dispatch, refreshCurrentReportsPage]);

    const openReportDetails = useCallback(async (row) => {
        setReportDetailsRow(row || null);
        setReportDetailsOpen(true);
        setReportDetailsError('');
        setReportDetailsPayload(null);

        const formID = String(row?.id ?? '').trim();
        const reportID = String(row?.associatedReport?.id ?? row?.reportID ?? '').trim();
        if (!formID || !reportID) {
            return;
        }

        setReportDetailsLoading(true);
        try {
            const { data } = await CrapExcel.getReportByID(formID, reportID);
            const payload = data?.data ?? data;
            setReportDetailsPayload(payload || null);
        } catch (err) {
            setReportDetailsError(
                err?.response?.data?.message || err?.message || 'Failed to load associated report details'
            );
        } finally {
            setReportDetailsLoading(false);
        }
    }, []);

    const linkedGeneratedReportsCount = reportsRows.filter((row) => Boolean(row.associatedReport)).length;
    const expectedGeneratedReportsMissingCount = reportsRows.filter(
        (row) => shouldHaveAssociatedReport(row) && !row.associatedReport
    ).length;
    const canGoToPreviousReportsPage = reportsQueryDone && reportsPage > 0 && !reportsLoading;
    const canGoToNextReportsPage = reportsQueryDone && Boolean(reportsLastKey) && !reportsLoading;

    useEffect(() => {
        if (mainTab !== 2) return;
        if (reportsLoading || reportsQueryDone) return;
        loadReportsFirstPage();
    }, [loadReportsFirstPage, mainTab, reportsLoading, reportsQueryDone]);

    return (
        <Box className="fade-in" sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1, sm: 0 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: (t) =>
                        t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'background.paper',
                }}
            >
                <Tabs
                    value={mainTab}
                    onChange={(_, v) => setMainTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        px: { xs: 1, sm: 2 },
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                    }}
                >
                    <Tab label="Upload to store" />
                    <Tab label="Excel store" />
                    <Tab label="All reports" />
                </Tabs>
            </Paper>

            {mainTab === 0 && (
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: (t) =>
                        t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'background.paper',
                }}
            >
                <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <CloudUploadOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={700}>
                            Upload to excel store
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Files are stored under <strong>excel-store/crap/</strong>. Review the full sheet grid, then
                            confirm upload.
                        </Typography>
                    </Box>
                </Stack>

                <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFiles(e.target.files)}
                />

                <Box
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onDrop={onDrop}
                    onClick={() => inputRef.current?.click()}
                    sx={{
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, background-color 0.2s',
                        bgcolor: 'action.hover',
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: (t) =>
                                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(25, 118, 210, 0.06)',
                        },
                    }}
                >
                    {parseLoading ? (
                        <Stack alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                            <LinearProgress sx={{ width: '100%', maxWidth: 280, borderRadius: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                Reading workbook…
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack alignItems="center" spacing={1}>
                            <Typography fontWeight={600}>Drop a spreadsheet here, or click to choose a file</Typography>
                            <Typography variant="caption" color="text.secondary">
                                .xlsx / .xls — a review step opens before upload
                            </Typography>
                        </Stack>
                    )}
                </Box>

            </Paper>
            )}

            {mainTab === 1 && (
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    Files in store
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Spreadsheet files in <code>excel-store/crap/</code>. Preview shows full sheet data (up to{' '}
                    {MAX_PREVIEW_ROWS_PER_SHEET.toLocaleString()} rows per sheet). Download saves the file; delete removes
                    it from S3 only.
                </Typography>

                {storeLoading ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                        <LinearProgress sx={{ width: 200 }} />
                    </Box>
                ) : storeItems.length === 0 ? (
                    <Alert severity="info">No spreadsheet files in this folder yet.</Alert>
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell>File</TableCell>
                                        <TableCell width={100} align="right">
                                            Size
                                        </TableCell>
                                        <TableCell width={200}>Modified (UTC)</TableCell>
                                        <TableCell width={168} align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {storeItems.map((row) => (
                                        <TableRow key={row.key} hover>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <InsertDriveFileOutlinedIcon
                                                        fontSize="small"
                                                        color="action"
                                                        sx={{ flexShrink: 0 }}
                                                    />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Tooltip title={row.key} placement="top-start">
                                                            <Typography fontWeight={600} noWrap>
                                                                {displayNameFromKey(row.key)}
                                                            </Typography>
                                                        </Tooltip>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right">{formatBytes(row.size)}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                {row.lastModified
                                                    ? new Date(row.lastModified).toLocaleString(undefined, {
                                                          timeZone: 'UTC',
                                                      })
                                                    : '—'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Preview all rows">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openStorePreview(row.key)}
                                                        aria-label="Preview"
                                                    >
                                                        <VisibilityOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Download">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => downloadObject(row.key)}
                                                        aria-label="Download"
                                                    >
                                                        <DownloadOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete from store">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => openDeleteDialog(row.key)}
                                                        aria-label="Delete"
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                {storeNextToken && (
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={storeLoadingMore}
                                                    onClick={() => loadMoreStore()}
                                                >
                                                    {storeLoadingMore ? 'Loading…' : 'Load more'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                )}
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Paper>
            )}

            {mainTab === 2 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        All CRAP reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Loads paginated monitoring feeds for form submissions and generated reports, then joins them by
                        form ID so each row shows form payload plus its associated generated report.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {reportsRows.length} row(s) · Page {reportsPage + 1} · Linked generated reports:{' '}
                            {linkedGeneratedReportsCount} · Generated report index size: {generatedReportsRows.length}
                        </Typography>
                    </Stack>
                    {expectedGeneratedReportsMissingCount > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {expectedGeneratedReportsMissingCount} record(s) with a report ID in this page do not have an
                            associated generated report in the indexed pages yet.
                        </Alert>
                    )}
                    {reportsLoading ? (
                        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                            <LinearProgress sx={{ width: 240 }} />
                        </Box>
                    ) : reportsRows.length === 0 ? (
                        <Alert severity="info">
                            {reportsQueryDone
                                ? 'No form submissions found.'
                                : 'Loading reports...'}
                        </Alert>
                    ) : (
                        <>
                            <TableContainer
                                sx={{
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'auto',
                                }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell width={48}>#</TableCell>
                                            <TableCell>Form id</TableCell>
                                            <TableCell>Report id</TableCell>
                                            <TableCell sx={{ minWidth: 180 }}>User / email</TableCell>
                                            <TableCell sx={{ minWidth: 150 }}>Payment</TableCell>
                                            <TableCell sx={{ minWidth: 260 }}>Form details</TableCell>
                                            <TableCell sx={{ minWidth: 240 }}>Associated report</TableCell>
                                            <TableCell width={132} align="right">
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportsRows.map((row, idx) => {
                                            const associated = row.associatedReport;
                                            const formData =
                                                row.formData && typeof row.formData === 'object' ? row.formData : {};
                                            const paymentSummary = getPaymentSummary(row);
                                            const reportVisibility = getReportVisibilityMeta(row);
                                            const canGenerateMissingReport =
                                                shouldHaveAssociatedReport(row) &&
                                                !associated &&
                                                (
                                                    String(row?.paymentStatus ?? '').trim().toUpperCase() === 'SUCCESS' ||
                                                    String(paymentSummary.orderStatus ?? '').trim().toUpperCase() === 'SUCCESS'
                                                );
                                            const isRegeneratingThisRow = regeneratingReportFormID === String(row?.id ?? '');
                                            return (
                                                <TableRow key={row.id || idx} hover>
                                                    <TableCell>{(reportsPage * REPORTS_PAGE_SIZE) + idx + 1}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                        {row.id || '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                        {row.reportID || '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                                                            {row.userID || '—'}
                                                        </Typography>
                                                        <Typography variant="body2">{row.email || '—'}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {row.paymentKind || 'main'} / {row.paymentStatus || '—'}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            {paymentSummary.amount} · {paymentSummary.mode}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            {paymentSummary.orderStatus} · {paymentSummary.transactionDate}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            Tracking: {paymentSummary.trackingID} · Order: {paymentSummary.orderID}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            Bank ref: {paymentSummary.bankRef}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {formData?.branch || formData?.course || row.course || '—'} ·{' '}
                                                            {formData?.specialisation || '—'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {formData?.jobRole || row.jobRole || '—'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Year: {formData?.year || '—'} · Country:{' '}
                                                            {formData?.countryToBuildCareer || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={reportVisibility.label}
                                                            color={reportVisibility.color}
                                                            sx={{ mb: 1 }}
                                                        />
                                                        {associated ? (
                                                            <>
                                                                <Typography sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                                                                    {associated.id}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {associated.paymentKind || 'main'} /{' '}
                                                                    {associated.paymentStatus || '—'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Dump size: {formatBytes(associated.reportSizeBytes)}
                                                                </Typography>
                                                                <Typography variant="caption" display="block" color="text.secondary">
                                                                    {reportVisibility.helperText}
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {reportVisibility.helperText}
                                                                </Typography>
                                                                {canGenerateMissingReport ? (
                                                                    <Button
                                                                        size="small"
                                                                        sx={{ mt: 1 }}
                                                                        onClick={() => regenerateReport(row)}
                                                                        disabled={reportsLoading || isRegeneratingThisRow}
                                                                    >
                                                                        {isRegeneratingThisRow ? 'Generating…' : 'Generate now'}
                                                                    </Button>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="column" spacing={1} alignItems="stretch">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => openReportDetails(row)}
                                                            >
                                                                View details
                                                            </Button>
                                                            {canGenerateMissingReport ? (
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => regenerateReport(row)}
                                                                    disabled={reportsLoading || isRegeneratingThisRow}
                                                                >
                                                                    {isRegeneratingThisRow ? 'Generating…' : 'Generate'}
                                                                </Button>
                                                            ) : null}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                    {(reportsPage > 0 || reportsLastKey) && (
                                        <TableFooter>
                                            <TableRow>
                                                <TableCell colSpan={9} sx={{ py: 0 }}>
                                                    <TablePagination
                                                        component="div"
                                                        count={-1}
                                                        page={reportsPage}
                                                        onPageChange={(_, nextPage) => {
                                                            if (nextPage > reportsPage) {
                                                                goToReportsPage(reportsPage + 1);
                                                            } else if (nextPage < reportsPage) {
                                                                goToReportsPage(reportsPage - 1);
                                                            }
                                                        }}
                                                        rowsPerPage={REPORTS_PAGE_SIZE}
                                                        rowsPerPageOptions={[REPORTS_PAGE_SIZE]}
                                                        labelRowsPerPage="Per page"
                                                        labelDisplayedRows={({ from, to }) =>
                                                            `${from}-${to} (page ${reportsPage + 1})`
                                                        }
                                                        backIconButtonProps={{ disabled: !canGoToPreviousReportsPage }}
                                                        nextIconButtonProps={{ disabled: !canGoToNextReportsPage }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableFooter>
                                    )}
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Paper>
            )}

            <Dialog
                open={reportDetailsOpen}
                onClose={() => {
                    if (!reportDetailsLoading) {
                        setReportDetailsOpen(false);
                    }
                }}
                maxWidth="lg"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>Form and report details</DialogTitle>
                <DialogContent dividers>
                    {reportDetailsLoading && <LinearProgress sx={{ mb: 2 }} />}
                    {reportDetailsRow ? (
                        <Stack spacing={2}>
                            {reportDetailsError ? <Alert severity="error">{reportDetailsError}</Alert> : null}
                            <Box>
                                <Chip
                                    size="small"
                                    label={getReportVisibilityMeta(reportDetailsRow).label}
                                    color={getReportVisibilityMeta(reportDetailsRow).color}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {getReportVisibilityMeta(reportDetailsRow).helperText}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Form submission (row)
                                </Typography>
                                <Box
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.hover',
                                        overflow: 'auto',
                                        maxHeight: 220,
                                        fontSize: 12,
                                    }}
                                >
                                    {prettyJson({
                                        id: reportDetailsRow.id,
                                        reportID: reportDetailsRow.reportID,
                                        userID: reportDetailsRow.userID,
                                        email: reportDetailsRow.email,
                                        paymentKind: reportDetailsRow.paymentKind,
                                        paymentStatus: reportDetailsRow.paymentStatus,
                                        orderID: reportDetailsRow.orderID,
                                        orderInfo: reportDetailsRow.orderInfo,
                                    })}
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Payment details
                                </Typography>
                                <Stack
                                    spacing={0.5}
                                    sx={{
                                        mb: 1.5,
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.hover',
                                    }}
                                >
                                    {(() => {
                                        const paymentSummary = getPaymentSummary(reportDetailsRow);
                                        return (
                                            <>
                                                <Typography variant="body2">
                                                    Status: {reportDetailsRow.paymentStatus || '—'} · Kind:{' '}
                                                    {reportDetailsRow.paymentKind || '—'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Amount: {paymentSummary.amount} · Mode: {paymentSummary.mode}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Order status: {paymentSummary.orderStatus} · Date:{' '}
                                                    {paymentSummary.transactionDate}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Tracking: {paymentSummary.trackingID} · Order: {paymentSummary.orderID}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Bank ref: {paymentSummary.bankRef}
                                                </Typography>
                                            </>
                                        );
                                    })()}
                                </Stack>
                                <Box
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.hover',
                                        overflow: 'auto',
                                        maxHeight: 220,
                                        fontSize: 12,
                                    }}
                                >
                                    {prettyJson(reportDetailsRow.paymentDetails || getPaymentDetails(reportDetailsRow) || null)}
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Form data payload
                                </Typography>
                                <Box
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.hover',
                                        overflow: 'auto',
                                        maxHeight: 260,
                                        fontSize: 12,
                                    }}
                                >
                                    {prettyJson(reportDetailsRow.formData || {})}
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Associated report index entry
                                </Typography>
                                <Box
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.hover',
                                        overflow: 'auto',
                                        maxHeight: 220,
                                        fontSize: 12,
                                    }}
                                >
                                    {prettyJson(reportDetailsRow.associatedReport || null)}
                                </Box>
                            </Box>

                            {reportDetailsPayload ? (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Generated report payload
                                    </Typography>
                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor: 'action.hover',
                                            overflow: 'auto',
                                            maxHeight: 380,
                                            fontSize: 12,
                                        }}
                                    >
                                        {prettyJson(reportDetailsPayload)}
                                    </Box>
                                </Box>
                            ) : (
                                !reportDetailsLoading &&
                                !reportDetailsError && (
                                    <Alert severity="info">
                                        Generated report details are not available for this record yet.
                                    </Alert>
                                )
                            )}
                        </Stack>
                    ) : (
                        <Alert severity="info">Select a record to inspect details.</Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    {reportDetailsRow &&
                    shouldHaveAssociatedReport(reportDetailsRow) &&
                    !reportDetailsRow.associatedReport &&
                    (
                        String(reportDetailsRow?.paymentStatus ?? '').trim().toUpperCase() === 'SUCCESS' ||
                        String(getPaymentSummary(reportDetailsRow).orderStatus ?? '').trim().toUpperCase() === 'SUCCESS'
                    ) ? (
                        <Button
                            variant="contained"
                            onClick={() => regenerateReport(reportDetailsRow)}
                            disabled={reportDetailsLoading || regeneratingReportFormID === String(reportDetailsRow?.id ?? '')}
                        >
                            {regeneratingReportFormID === String(reportDetailsRow?.id ?? '') ? 'Generating…' : 'Generate report'}
                        </Button>
                    ) : null}
                    <Button
                        onClick={() => {
                            if (reportDetailsLoading) return;
                            setReportDetailsOpen(false);
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={uploadReviewOpen}
                onClose={() => {
                    if (!uploading) {
                        resetStagedUpload();
                    }
                }}
                maxWidth="xl"
                fullWidth
                scroll="paper"
            >
                <DialogTitle sx={{ pr: 6 }}>
                    Review before upload
                    {stagedFile ? (
                        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ mt: 0.5 }}>
                            {stagedFile.name} · {formatBytes(stagedFile.size)}
                        </Typography>
                    ) : null}
                </DialogTitle>
                <DialogContent dividers>
                    {uploading && <LinearProgress sx={{ mb: 2 }} />}
                    <ExcelPreviewSheetTabs sheets={sheetPreview} />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button
                        onClick={() => {
                            if (!uploading) resetStagedUpload();
                        }}
                        disabled={uploading}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => uploadToStore()}
                        disabled={uploading || !stagedFile || !stagedFile.size}
                    >
                        {uploading ? 'Uploading…' : 'Confirm upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={storePreviewOpen} onClose={closeStorePreview} maxWidth="xl" fullWidth scroll="paper">
                <DialogTitle>{storePreviewTitle || 'Preview'}</DialogTitle>
                <DialogContent dividers>
                    {storePreviewLoading ? (
                        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                            <LinearProgress sx={{ width: '60%' }} />
                        </Box>
                    ) : (
                        <ExcelPreviewSheetTabs sheets={storePreviewSheets} />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
                    {storePreviewKey ? (
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => {
                                openDeleteDialog(storePreviewKey);
                            }}
                        >
                            Delete from store
                        </Button>
                    ) : null}
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={closeStorePreview}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Remove file?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Delete{' '}
                        <strong>{deleteTargetKey ? displayNameFromKey(deleteTargetKey) : ''}</strong> from the excel store?
                        This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => confirmDeleteFromStore()}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
